import { useEffect, useRef, useState } from 'react';
import { isToss } from '../lib/platform';
import { loadTossSdk } from '../lib/toss';
import { clientConfig } from '../lib/config';

/**
 * 앱인토스 배너(디스플레이) 광고.
 *
 * 왜 필요한가: 리워드 광고는 **횟수를 다 쓴 사람만** 본다. 들어와서 한두 번 보고 나가는
 * 대다수에게서는 수익이 0이다. 배너는 그 구간을 메운다.
 *
 * **웹에서는 아무것도 렌더하지 않는다** — 웹 표면의 광고는 애드센스다. 둘은 같은 화면에
 * 함께 둘 수 없다 (doc/apps-in-toss.md §4).
 *
 * ## 배치 규칙 (토스 심사 기준)
 *  - 한 화면에 **같은 포맷 2개 이상 금지** → 한 화면에 이 컴포넌트는 하나만 둘 것
 *  - 광고와 콘텐츠의 구분이 흐리면 안 된다 → 구분선을 두고 '광고'라고 적는다
 *  - 버튼 같은 조작 요소 **바로 옆에 붙이지 말 것** (실수 클릭 유도로 본다) → 위쪽 여백을 크게
 *
 * 광고 그룹 ID는 번들이 아니라 **서버 설정(GET /config)** 에서 온다. 번들에 박으면 광고를
 * 켜고 끄는 데 영업일 3~5일짜리 심사가 통째로 들기 때문이다. 리워드와는 **다른 그룹**이다.
 */

/** 초기화 응답이 안 오면 포기하는 시간 — 광고 하나 때문에 화면이 기다릴 이유는 없다 */
const INIT_TIMEOUT_MS = 5_000;

type TossSdk = NonNullable<Awaited<ReturnType<typeof loadTossSdk>>>;

/**
 * `TossAds.initialize`는 앱을 통틀어 **한 번만** 부른다.
 * 화면마다 배너가 있어도 초기화는 하나이므로 약속을 모듈에 담아 돌려 쓴다.
 */
let initialized: Promise<boolean> | null = null;

function initTossAds(sdk: TossSdk): Promise<boolean> {
  initialized ??= new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), INIT_TIMEOUT_MS);
    const settle = (ok: boolean) => {
      clearTimeout(timer);
      resolve(ok);
    };
    try {
      sdk.TossAds.initialize({
        callbacks: {
          onInitialized: () => settle(true),
          onInitializationFailed: () => settle(false),
        },
      });
    } catch {
      settle(false); // 광고 초기화 실패는 화면에 영향을 주지 않는다
    }
  });
  return initialized;
}

export function TossBanner() {
  const hostRef = useRef<HTMLDivElement | null>(null);
  /**
   * 광고가 **실제로 그려졌을 때만** 구분선과 '광고' 표시를 낸다.
   * 안 채워졌는데(onNoFill) 빈 테두리만 남으면 화면이 고장 난 것처럼 보인다.
   */
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (!isToss) return;
    let cancelled = false;
    let detach: (() => void) | null = null;

    void (async () => {
      // 설정과 SDK를 함께 기다린다 — 둘 중 하나만 있어도 배너는 못 붙인다
      const [sdk, cfg] = await Promise.all([loadTossSdk(), clientConfig()]);
      const adGroupId = cfg.tossBannerAdGroupId;
      if (cancelled || !sdk || !adGroupId || !hostRef.current) return;
      // 구버전 토스 앱 — 조용히 넘어간다
      if (!sdk.TossAds.attachBanner.isSupported()) return;
      if (!(await initTossAds(sdk))) return;
      if (cancelled || !hostRef.current) return;

      const { destroy } = sdk.TossAds.attachBanner(adGroupId, hostRef.current, {
        theme: 'auto', // 보는 사람의 테마를 따른다
        /**
         * 우리 화면은 전부 둥근 카드다. 기본값 `expanded`는 가로로 꽉 차서 광고만 화면 끝까지
         * 뻗어 나간다 — 그 자리만 다른 앱처럼 보인다. 콘솔의 강조 유형(문구/이미지)과는 무관한,
         * 순수한 레이아웃 설정이다.
         */
        variant: 'card',
        callbacks: {
          onAdRendered: () => !cancelled && setRendered(true),
          // 채울 광고가 없거나 그리기에 실패 — 자리를 비운다
          onNoFill: () => !cancelled && setRendered(false),
          onAdFailedToRender: () => !cancelled && setRendered(false),
        },
      });
      detach = destroy;
    })();

    return () => {
      cancelled = true;
      detach?.(); // 화면을 떠나면 반드시 거둔다 — 안 그러면 슬롯이 남는다
    };
  }, []);

  if (!isToss) return null;

  return (
    // 위쪽 여백을 넉넉히 — 바로 위 버튼과 붙으면 실수로 눌린다
    <div className="mt-8">
      {rendered && (
        <p className="mb-1.5 border-t border-white/10 pt-4 text-center text-[10px] text-moon-dim">
          광고
        </p>
      )}
      {/* SDK가 이 안에 광고를 그린다. 안 채워지면 높이 0으로 남아 자리를 차지하지 않는다 */}
      <div ref={hostRef} />
    </div>
  );
}
